import React, { Children } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/ui/Footer';
import { Database, Target, Shield, Users, Building2, FileText, TrendingUp, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.8,
    ease: [0.22, 1, 0.36, 1]
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};
export function WhatWeDoPage() {
  return <div className="bg-black min-h-screen" data-id="element-491">
      <Header data-id="element-492" />
      <main className="pt-32 pb-24 px-6" data-id="element-493">
        <div className="max-w-7xl mx-auto" data-id="element-494">
          <motion.div className="mb-16" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-495">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6" data-id="element-496">
              What We Do
            </h1>
            <p className="text-xl text-white/80 max-w-3xl leading-relaxed" data-id="element-497">
              We transform healthcare data chaos into strategic clarity through
              Master Data Management.
            </p>
          </motion.div>
          <motion.div className="grid lg:grid-cols-3 gap-8 mb-16" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
          once: true,
          margin: '-100px'
        }} data-id="element-498">
            {[{
            icon: Target,
            title: 'Discover',
            description: 'We start by uncovering the hidden costs in your data ecosystem:',
            items: ['Duplicate spend and missed billing opportunities', 'Manual rework and operational inefficiencies', 'Compliance risks and regulatory gaps'],
            color: 'purple'
          }, {
            icon: Database,
            title: 'Align',
            description: 'We create golden records and strategic roadmaps across:',
            items: ['Provider and patient master data', 'Payer relationships and networks', 'Location hierarchies and service lines'],
            color: 'cyan'
          }, {
            icon: Shield,
            title: 'Deploy',
            description: 'We implement hands-on, so your teams can focus on care:',
            items: ['Full implementation and change management', 'Training and knowledge transfer', 'Ongoing support and optimization'],
            color: 'purple'
          }].map((item, index) => <motion.div key={index} variants={fadeInUp} className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8" data-id="element-499">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center mb-6" data-id="element-500">
                  <item.icon className="w-8 h-8 text-white" data-id="element-501" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4" data-id="element-502">
                  {item.title}
                </h3>
                <p className="text-white/70 leading-relaxed mb-4" data-id="element-503">
                  {item.description}
                </p>
                <ul className="space-y-2" data-id="element-504">
                  {item.items.map((listItem, idx) => <li key={idx} className="flex items-start gap-2 text-white/70" data-id="element-505">
                      <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" data-id="element-506" />
                      <span data-id="element-507">{listItem}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </motion.div>
          <motion.div className="mb-16" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-508">
            <h2 className="text-3xl md:text-4xl font-medium text-white mb-8 text-center" data-id="element-509">
              Data Domains We Master
            </h2>
            <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" variants={staggerContainer} initial="initial" whileInView="animate" viewport={{
            once: true,
            margin: '-100px'
          }} data-id="element-510">
              {[{
              icon: Users,
              title: 'Provider Data',
              desc: 'Physician, nurse, and staff master data management'
            }, {
              icon: Users,
              title: 'Patient/Member Data',
              desc: 'Patient identity resolution and member matching'
            }, {
              icon: Building2,
              title: 'Locations & Networks',
              desc: 'Facility hierarchies and network relationships'
            }, {
              icon: FileText,
              title: 'Claims/Billing',
              desc: 'Revenue cycle and claims data accuracy'
            }, {
              icon: Shield,
              title: 'Compliance & Reporting',
              desc: 'Regulatory compliance and audit readiness'
            }, {
              icon: TrendingUp,
              title: 'Analytics & Insights',
              desc: 'Data-driven decision making and reporting'
            }].map((item, index) => <motion.div key={index} variants={fadeInUp} className="bg-white/5 border border-white/10 rounded-xl p-6" data-id="element-511">
                  <item.icon className="w-10 h-10 text-white/70 mb-4" data-id="element-512" />
                  <h3 className="text-lg font-semibold text-white mb-2" data-id="element-513">
                    {item.title}
                  </h3>
                  <p className="text-sm text-white/60" data-id="element-514">{item.desc}</p>
                </motion.div>)}
            </motion.div>
          </motion.div>
          <motion.div className="bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/10 rounded-2xl p-8 lg:p-12" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.3,
          ease: [0.22, 1, 0.36, 1]
        }} whileHover={{
          scale: 1.01
        }} data-id="element-515">
            <div className="max-w-3xl mx-auto text-center" data-id="element-516">
              <h2 className="text-3xl font-semibold text-white mb-6" data-id="element-517">
                Why Healthcare Organizations Choose Us
              </h2>
              <p className="text-lg text-white/80 leading-relaxed mb-8" data-id="element-518">
                We don't just design strategy. We do the messy work—cleaning,
                aligning, and syncing your data—so your clinicians, finance, and
                ops teams run on the same trusted numbers.
              </p>
              <motion.a href="https://vantagedatagroup.com/contact" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center h-12 rounded-xl px-8 text-sm bg-white text-black font-semibold hover:bg-white/90 transition-colors" whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-519">
                Schedule a Consultation
              </motion.a>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-520" />
    </div>;
}